import { useState, useMemo } from 'react'
import { X, MapPin, Clock, Star, Copy, ChevronDown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogClose,
  Button,
} from '@/components/ui'
import { restaurantConfig } from '@/config/restaurant'
import { MAPBOX_TOKEN } from '@/config/mapbox'
import type { RestaurantInfo, RestaurantHours } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

/**
 * Convert "HH:MM" 24-hour string to "H:MM AM/PM" display format
 */
function formatTime24to12(time24: string): string {
  const [hourStr, minuteStr] = time24.split(':')
  let hour = parseInt(hourStr, 10)
  const minute = minuteStr || '00'
  const period = hour >= 12 ? 'PM' : 'AM'
  if (hour === 0) hour = 12
  else if (hour > 12) hour -= 12
  return `${hour}:${minute} ${period}`
}

/**
 * Get the current day's hours entry from API hours.
 * JS getDay() returns 0=Sunday, but API uses 0=Monday, so we convert.
 */
function getTodayHours(hours: RestaurantHours[]): RestaurantHours | undefined {
  const jsDay = new Date().getDay() // 0=Sun, 1=Mon, ..., 6=Sat
  const apiDay = jsDay === 0 ? 6 : jsDay - 1 // Convert to 0=Mon, ..., 6=Sun
  return hours.find((h) => h.day_of_week === apiDay)
}

/**
 * Build a formatted hours list from API data for display.
 * Returns an array of { day, hours } matching the hardcoded fallback shape.
 */
function buildHoursDisplay(apiHours: RestaurantHours[]): Array<{ day: string; hours: string }> {
  // Sort by day_of_week to ensure Monday-Sunday order
  const sorted = [...apiHours].sort((a, b) => a.day_of_week - b.day_of_week)
  return sorted.map((entry) => ({
    day: DAY_NAMES[entry.day_of_week] || `Day ${entry.day_of_week}`,
    hours: entry.is_closed
      ? 'Closed'
      : `${formatTime24to12(entry.open_time)} - ${formatTime24to12(entry.close_time)}`,
  }))
}

interface RestaurantInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  restaurant: RestaurantInfo | null
}

export function RestaurantInfoDialog({
  isOpen,
  onClose,
  restaurant,
}: RestaurantInfoDialogProps) {
  const [isHoursExpanded, setIsHoursExpanded] = useState(false)
  const { location: selectedLocation, address: locationAddress } = useSelectedLocation()

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress)
  }

  const fullAddress = locationAddress

  // Read primary color from CSS variable for Mapbox static pin
  const pinHex = useMemo(() => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
    const [h, s, l] = raw.split(/\s+/).map(parseFloat)
    const sf = (s || 0) / 100, lf = (l || 0) / 100
    const a = sf * Math.min(lf, 1 - lf)
    const f = (n: number) => {
      const k = (n + (h || 0) / 30) % 12
      return Math.round(255 * (lf - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)))
        .toString(16).padStart(2, '0')
    }
    return `${f(0)}${f(8)}${f(4)}`
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const mapLat = selectedLocation?.latitude ?? restaurantConfig.mapCoordinates.lat
  const mapLng = selectedLocation?.longitude ?? restaurantConfig.mapCoordinates.lng
  const mapUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s+${pinHex}(${mapLng},${mapLat})/${mapLng},${mapLat},14,0/600x300@2x?access_token=${MAPBOX_TOKEN}`

  const cuisineTags = restaurant?.cuisine_tags?.join(' \u00B7 ')

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md w-full max-h-[90vh] flex flex-col !p-0 gap-0 overflow-hidden" hideCloseButton>
        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide overscroll-contain">
          {/* Floating close — sticky, zero-height so map starts at top */}
          <div className="sticky top-0 z-20 pointer-events-none h-0 overflow-visible">
            <div className="flex justify-end p-3">
              <DialogClose className="pointer-events-auto w-10 h-10 rounded-full bg-black/30 dark:bg-white/20 backdrop-blur-xl text-white shadow-lg flex items-center justify-center cursor-pointer hover:bg-black/50 dark:hover:bg-white/30 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </div>

          {/* Hero map with cinematic gradient */}
          <div className="relative h-48 bg-muted overflow-hidden">
            <img
              src={mapUrl}
              alt="Restaurant location map"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          </div>

          {/* Content — emerges from gradient */}
          <div className="relative px-6 pb-6 -mt-8 pt-4">
            {/* Restaurant Name & Categories */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">
                {restaurant?.title || 'Restaurant'}
              </h2>
              {cuisineTags && (
                <p className="text-sm text-muted-foreground">{cuisineTags}</p>
              )}
            </div>

            {/* Address */}
            <div className="flex items-start gap-3 pb-6 border-b border-border">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm">{fullAddress}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 flex-shrink-0 rounded-full"
                onClick={handleCopyAddress}
                title="Copy address"
                aria-label="Copy restaurant address"
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>

            {/* Hours */}
            <HoursSection
              restaurant={restaurant}
              isExpanded={isHoursExpanded}
              onToggle={() => setIsHoursExpanded(!isHoursExpanded)}
            />

            {/* Rating */}
            <div className="flex items-center gap-3 pt-6">
              <Star className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <p className="text-sm">
                {restaurant?.rating || '4.6'} ({restaurant?.review_count || '130+'} ratings)
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Hours section — uses API hours when available, falls back to hardcoded config.
 * Also checks hours_exceptions for today's date overrides.
 */
function HoursSection({
  restaurant,
  isExpanded,
  onToggle,
}: {
  restaurant: RestaurantInfo | null
  isExpanded: boolean
  onToggle: () => void
}) {
  const apiHours = restaurant?.hours
  const hasApiHours = apiHours && apiHours.length > 0

  // Determine the "Open until" / "Closed" status text for the header
  const statusText = useMemo(() => {
    if (!hasApiHours) {
      // Fallback: use static text (no dynamic calculation from hardcoded strings)
      return 'See hours'
    }

    // Check if today has an hours_exception override
    const todayStr = new Date().toISOString().split('T')[0] // "YYYY-MM-DD"
    const exception = restaurant?.hours_exceptions?.find((ex) => ex.date === todayStr)

    if (exception) {
      if (exception.is_closed) return `Closed today \u2013 ${exception.label}`
      if (exception.open_time && exception.close_time) {
        return `Open until ${formatTime24to12(exception.close_time)}`
      }
    }

    const today = getTodayHours(apiHours!)
    if (!today || today.is_closed) return 'Closed today'
    return `Open until ${formatTime24to12(today.close_time)}`
  }, [hasApiHours, apiHours, restaurant?.hours_exceptions])

  // Build the expanded hours rows
  const hoursRows = useMemo(() => {
    if (hasApiHours) return buildHoursDisplay(apiHours!)
    // Fallback to hardcoded config
    return Object.entries(restaurantConfig.hours).map(([day, hours]) => ({
      day,
      hours,
    }))
  }, [hasApiHours, apiHours])

  return (
    <div className="py-6 border-b border-border">
      <Button
        variant="ghost"
        className="w-full flex items-start gap-3 h-auto text-left justify-start p-0 hover:bg-transparent"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <Clock className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">{statusText}</p>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-muted-foreground transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </Button>

      {isExpanded && (
        <div className="mt-4 ml-8 space-y-2 text-sm">
          {hoursRows.map(({ day, hours }) => (
            <div key={day} className="flex justify-between">
              <span className="capitalize text-muted-foreground">{day}</span>
              <span>{hours}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
